import { useMemo } from 'react';
import { useStores } from '~/features/checkout/hooks/useStores';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { useLocalStorage } from '~/shared/hooks/useLocalStorage';
import { composeDefaultStores, resolveStoreParams } from './reservationStoreSorting';

/**
 * Resolves the SFCC store-sorting `type` from the current customer, fetches the store list
 * (already sorted server-side), and composes the default view.
 *
 * - `instore` (store personnel) / `rmembers` (Plus members): distance-sorted around the
 *   customer's postal code by SFCC.
 * - `anonymous` (guests, and logged-in customers without a postal code): city/weight-sorted.
 *
 * `anonymous` and `rmembers` additionally get their previously-selected C&R/C&C stores prepended
 * (once, deduped); `instore` keeps the server order.
 *
 * `allStores` is the raw server list (used for client-side geo/search re-sorting); `defaultStores`
 * is the composed list for the resting view. Both keep the  pinned-store prioritization.
 *
 * The type resolution and prepend/dedup logic live in `./reservationStoreSorting` as pure functions
 */
export const useReservationStores = ({ pid }: { pid?: string }) => {
    const customer = useCustomer();
    const postalCode = customer.addresses?.[0]?.postalCode ?? '';

    const [prevCRStoreId] = useLocalStorage<string>('previouslySelectedCRStoreId');
    const [prevCCStoreId] = useLocalStorage<string>('previouslySelectedCCStoreId');

    const queryParams = resolveStoreParams({
        pid,
        isStorePersonnel: customer.isStorePersonnel,
        isPlusMember: customer.isPlusMember,
        postalCode,
    });

    const { data: storesResponse, isLoading } = useStores(queryParams);

    const allStores = storesResponse?.data;

    const defaultStores = useMemo(() => {
        return composeDefaultStores({
            stores: allStores,
            type: queryParams.type,
            prevCRStoreId,
            prevCCStoreId,
        });
    }, [allStores, queryParams.type, prevCRStoreId, prevCCStoreId]);

    return { allStores, defaultStores, isLoading };
};
